import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import pushNotifications from '../Utils/pushNotifications';
import { showToast } from '../Utils/toast';

const NotificationPromptModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkShouldShow();
  }, []);

  const checkShouldShow = async () => {
    // Don't show if user dismissed it before
    const dismissed = localStorage.getItem('notification_prompt_dismissed');
    if (dismissed === 'true') {
      return;
    }

    // Don't show if notifications not supported
    if (!pushNotifications.isSupported()) {
      return;
    }

    // Don't show if already subscribed
    const isSubscribed = await pushNotifications.isSubscribed();
    if (isSubscribed) {
      return;
    }

    // Don't show if permission was denied
    const permission = pushNotifications.getPermissionStatus();
    if (permission === 'denied') {
      return;
    }

    // Show modal after a short delay (better UX)
    setTimeout(() => {
      setIsOpen(true);
    }, 2000);
  };

  const handleEnable = async () => {
    setLoading(true);
    try {
      await pushNotifications.subscribe();
      showToast.success('Notifications enabled! 🔔');
      setIsOpen(false);
      localStorage.setItem('notification_prompt_dismissed', 'true');
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      if (error.message.includes('permission')) {
        showToast.error('Please allow notifications in your browser');
      } else if (error.message.includes('push service')) {
        showToast.error('Unable to connect to push service. You can try again from your profile settings.');
      } else {
        showToast.error('Failed to enable notifications. You can try again from your profile.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismissOrLater = () => {
    setIsOpen(false);
    // Mark as dismissed so it won't show again
    localStorage.setItem('notification_prompt_dismissed', 'true');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleDismissOrLater}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-primary rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-scale-in">
          {/* Close button */}
          <button
            onClick={handleDismissOrLater}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-accent to-accent rounded-full flex items-center justify-center">
              <Bell className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-center text-white mb-3">
            Stay Updated! 🎵
          </h2>

          {/* Description */}
          <p className="text-center text-gray-300 mb-6 leading-relaxed">
            Get notified when people you follow add new songs, when you get new followers, and more.
            Never miss a beat!
          </p>

          {/* Features list */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm text-gray-300">
                New songs from people you follow
              </span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm text-gray-300">
                When you get new followers
              </span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm text-gray-300">
                App updates and announcements
              </span>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleEnable}
              disabled={loading}
              className="w-full bg-gradient-to-r from-accent to-accent hover:from-accent hover:to-accent text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? 'Enabling...' : 'Enable Notifications'}
            </button>

            <button
              onClick={handleDismissOrLater}
              className="w-full bg-transparent hover:bg-secondary text-gray-300 font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Maybe Later
            </button>
          </div>

          {/* Footer note */}
          <p className="text-xs text-center text-gray-400 mt-4">
            You can always enable or disable notifications from your profile settings
          </p>
        </div>
      </div>

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default NotificationPromptModal;
